import { useState } from "react";
import axios from "axios";
import { Terminal, LogIn, UserPlus, Key, Loader2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Login = ({ onLogin }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    accessCode: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isLogin) {
        // Login
        const response = await axios.post(`${API}/auth/login`, {
          username: formData.username,
          password: formData.password
        });
        
        if (response.data.success) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.user));
          toast.success(`Welcome back, ${response.data.user.username}!`);
          onLogin(response.data.user, response.data.token);
        }
      } else {
        // Register with access code
        const response = await axios.post(`${API}/auth/register`, {
          username: formData.username,
          password: formData.password,
          access_code: formData.accessCode
        });
        
        if (response.data.success) {
          localStorage.setItem("token", response.data.token);
          localStorage.setItem("user", JSON.stringify(response.data.user));
          toast.success(`Account created! Welcome, ${response.data.user.username}!`);
          onLogin(response.data.user, response.data.token);
        }
      }
    } catch (error) {
      console.error("Auth error:", error);
      toast.error(error.response?.data?.detail || "Authentication failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center p-3 bg-green-500/10 border border-green-500/30 mb-4">
            <Terminal className="w-8 h-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-mono font-bold text-white">
            SEARCH<span className="text-green-500">A</span>LEAD
          </h1>
          <p className="text-zinc-500 text-sm font-mono mt-2">
            B2B Lead Generation Platform
          </p>
        </div>

        {/* Login/Register Card */}
        <div className="bg-zinc-900/50 border border-zinc-800 p-6">
          {/* Tabs */}
          <div className="flex mb-6 border-b border-zinc-800">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 pb-3 text-sm font-mono uppercase tracking-wider transition-colors ${
                isLogin 
                  ? "text-green-500 border-b-2 border-green-500" 
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <LogIn className="w-4 h-4 inline mr-2" />
              Login
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 pb-3 text-sm font-mono uppercase tracking-wider transition-colors ${
                !isLogin 
                  ? "text-green-500 border-b-2 border-green-500" 
                  : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <UserPlus className="w-4 h-4 inline mr-2" />
              Register
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                Username
              </Label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-12 font-mono"
                placeholder="Enter username"
                required
                data-testid="username-input"
              />
            </div>

            <div>
              <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                Password
              </Label>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-12 font-mono pr-10"
                  placeholder="Enter password"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div>
                <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500">
                  <Key className="w-3 h-3 inline mr-1" />
                  Access Code
                </Label>
                <Input
                  type="text"
                  value={formData.accessCode}
                  onChange={(e) => setFormData({ ...formData, accessCode: e.target.value.toUpperCase() })}
                  className="mt-1 bg-zinc-900 border-zinc-700 rounded-none h-12 font-mono uppercase"
                  placeholder="Enter access code"
                  required={!isLogin}
                  data-testid="access-code-input"
                />
                <p className="text-xs text-zinc-600 mt-1">
                  Contact administrator to get an access code
                </p>
              </div>
            )}

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-green-500 hover:bg-green-600 text-black font-mono uppercase tracking-wider rounded-none mt-6"
              data-testid="submit-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {isLogin ? "Logging in..." : "Creating account..."}
                </>
              ) : (
                <>
                  {isLogin ? <LogIn className="w-4 h-4 mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                  {isLogin ? "Login" : "Create Account"}
                </>
              )}
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-zinc-600 mt-6 font-mono">
          Protected access only • Unauthorized use prohibited
        </p>
      </div>
    </div>
  );
};

export default Login;
